import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useState } from 'react';
import type { HttpError } from 'restify-errors';
import type {
  SemanticCOLORS,
  SemanticICONS,
  SemanticSIZES,
} from 'semantic-ui-react';
import { Segment, Grid, Label, Image, Icon, Button } from 'semantic-ui-react';

import type { ServiceError } from '@models';
import Link from '@components/link';

import s from './err.scss';

type ErrorMessage = string | JSX.Element;

export type KnownErrorTypes =
  | 'Forbidden'
  | 'NotFound'
  | 'InternalServer'
  | 'GraphQL'
  | 'UnknownError'
  | 'Unavailable';

// Discourage passing an unknown errorType without an error object or message
type ConditionalErrorProps =
  | { error?: ServiceError; message: ErrorMessage; errorType?: KnownErrorTypes }
  | {
      // | undefined ensures that error has to be passed but can be undefined. Useful in places where a separate variable defines an error state
      error: ServiceError | FetchBaseQueryError | undefined;
      message?: ErrorMessage;
      errorType?: KnownErrorTypes;
    }
  | {
      error?: ServiceError;
      message?: ErrorMessage;
      errorType: KnownErrorTypes;
    };

export type ErrProps = {
  className?: string;
  header?: string | JSX.Element;
  headerColor?: SemanticCOLORS;
  image?: string;
  imageSize?: SemanticSIZES;
  route?: string | null;
  routeIcon?: SemanticICONS | null;
  routeMsg?: string | null;
  base?: boolean;
  inline?: boolean;
  noBorder?: boolean;
  onRetryButtonClick?: () => void;
  isRetrying?: boolean;
} & ConditionalErrorProps;

interface ErrDefaults {
  statusCode?: number;
  header: string | JSX.Element;
  headerColor: SemanticCOLORS;
  image: string;
  imageSize?: SemanticSIZES;
  message: string | JSX.Element;
}

type ErrSettings = ErrProps & ErrDefaults;

// Provide defaults based on type for consistency ('NotFound', 'InternalServer', etc.)
const getDefaults = (type: KnownErrorTypes | string | null): ErrDefaults => {
  switch (type) {
    case 'Forbidden': {
      return {
        statusCode: 403,
        header: 'Forbidden',
        headerColor: 'red',
        image: 'spacecat_stop.svg',
        imageSize: 'medium',
        message:
          "We're really sorry but you don't have permission to access that resource.",
      };
    }
    case 'NotFound': {
      return {
        statusCode: 404,
        header: 'Not Found',
        headerColor: 'violet',
        image: 'robot_notfound.svg',
        imageSize: 'small',
        message: "We're really sorry but we could not locate that resource.",
      };
    }
    case 'InternalServer': {
      return {
        statusCode: 500,
        header: 'Service Error',
        headerColor: 'red',
        image: 'robot_dismantled.svg',
        imageSize: 'large',
        message:
          "We're really sorry but we weren't able to display this page because of a data retrieval error.",
      };
    }
    case 'GraphQL': {
      return {
        header: type,
        headerColor: 'teal',
        image: 'robot_nuke.svg',
        imageSize: 'medium',
        message:
          "We're really sorry but we encountered an error with the reporting service. Please try again...",
      };
    }
    default: {
      return {
        header: type || 'Unknown Error',
        headerColor: 'teal',
        image: 'robot_dismantled.svg',
        imageSize: 'large',
        message:
          "We're really sorry but we weren't able to display this page because of a data retrieval error.",
      };
    }
  }
};

// Unify settings - props provided > error object message > defaults
const unifySettings = (
  defaults: ReturnType<typeof getDefaults>,
  errInfo: object,
  props: ErrProps,
): ErrSettings => {
  // Remove any null values from props
  const propsCopy = Object.entries(props)
    .filter(item => item[1] !== null)
    .reduce((obj, curr) => {
      const [key, value] = curr;
      return Object.assign(obj, { [key]: value });
    }, {});

  return { ...defaults, ...errInfo, ...propsCopy };
};

const parseError = (
  err?: ServiceError | FetchBaseQueryError | null,
  errorType?: string,
) => {
  if (!err)
    return {
      err,
      type: errorType || null,
    };

  const type =
    errorType ||
    ('type' in err && err.type) ||
    ('data' in err && (err.data as HttpError).code) ||
    null;

  const statusCode =
    typeof err === 'object' &&
    (('statusCode' in err && err.statusCode) ||
      ('status' in err && err.status));

  let errMsg;
  let detail;

  if ('message' in err && err.message) {
    // See if the error message provided is parseable
    try {
      const parsedErr = JSON.parse(err.message);
      detail = parsedErr.detail;
      errMsg = parsedErr.message;
    } catch {
      errMsg = err.message || false;
    }
  } else if ('error' in err && err.error) {
    errMsg = err.error;
    if ('data' in err && err.data) detail = err.data;
  }

  return {
    err,
    type,
    statusCode,
    errMsg,
    detail,
  };
};

export const Err = ({ isRetrying, onRetryButtonClick, ...props }: ErrProps) => {
  useStyles(s);

  const [showDetail, setShowDetail] = useState(false);

  const toggleDetails = () => {
    setShowDetail(prevState => !prevState);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    const isEnterKey = e.type === 'keydown' && e.keyCode === 13;
    if (isEnterKey) toggleDetails();
    return this;
  };

  const { err, errMsg, statusCode, type, detail } = parseError(
    props.error,
    props.errorType,
  );

  const defaults = getDefaults(type);
  const settings = unifySettings(
    defaults,
    err ? { statusCode, message: errMsg } : {},
    // Prevents no-unused-props giving false positive
    // when passing whole props object to helper function
    props,
  );

  const {
    className,
    header,
    headerColor,
    image,
    imageSize,
    message,
    route,
    routeIcon,
    routeMsg,
    base,
    inline,
    noBorder,
    statusCode: code,
  } = settings;

  return (
    <Segment.Group
      className={classNames({
        [s.noBoxShadow]: !base,
        [s.noBorder]: noBorder,
      })}
    >
      <Segment
        style={{
          overflow: 'hidden',
        }}
        className={classNames(
          className,
          s.root,
          base ? s.base : s.noBoxShadow,
          inline ? s.inline : '',
        )}
      >
        <Grid
          verticalAlign="middle"
          centered
          columns={1}
          style={{ height: '100%', width: '100%' }}
        >
          <Grid.Row>
            <Grid.Column textAlign="center">
              <div style={{ margin: '0 0 2rem 0' }}>
                <Label
                  size="huge"
                  color={headerColor}
                  pointing="below"
                  style={{ padding: '2rem', margin: '1rem 0 2rem 0' }}
                >
                  <em>&lt;Beep Boop&gt;</em>
                  &nbsp;
                  {code && `${code}—`}
                  {header}
                </Label>
                <Image
                  size={imageSize || 'large'}
                  centered
                  src={`/img/${image}`}
                />
              </div>
              <Segment.Group
                className={s.noBoxShadow}
                style={{
                  marginBottom: '1rem',
                }}
              >
                <Segment
                  size="large"
                  secondary
                  className={classNames(s.noBoxShadow, s.errorMsgSegment)}
                >
                  {message}
                  {detail && Object.keys(detail).length !== 0 && (
                    <Button
                      floated="right"
                      color="blue"
                      disabled={showDetail}
                      className={s.detailLink}
                      onClick={toggleDetails}
                      onKeyDown={handleKeyDown}
                      tabIndex="0"
                    >
                      Show Details
                    </Button>
                  )}
                  {detail && showDetail && (
                    <Segment>
                      <pre className={s.jsonStrFmt}>
                        {JSON.stringify(detail, null, 2)}
                      </pre>
                    </Segment>
                  )}
                </Segment>
                {route && (
                  <Segment className={s.noBoxShadow}>
                    <Link to={route}>
                      {routeIcon && <Icon name={routeIcon} />}
                      {routeMsg}
                    </Link>
                  </Segment>
                )}
                {onRetryButtonClick && (
                  <Segment className={s.noBoxShadow}>
                    <Button
                      primary
                      compact
                      size="small"
                      onClick={onRetryButtonClick}
                      loading={isRetrying}
                      disabled={isRetrying}
                    >
                      <Icon name="redo" />
                      Retry
                    </Button>
                  </Segment>
                )}
              </Segment.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    </Segment.Group>
  );
};

export default Err;
